import React from "react";
import { Building2, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useAccount, Account } from "@/contexts/account.context";

interface AccountSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Desabilita o redirecionamento automático após trocar de conta. Padrão: true. */
  redirectOnSwitch?: boolean;
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  open,
  onOpenChange,
  redirectOnSwitch = true,
}) => {
  const {
    selectedAccount,
    accounts,
    switchAccount,
    clearSelectedAccount,
    loading,
    isAgencyUser,
    canSwitchAccounts,
    isTeamScoped,
  } = useAccount();

  // Para quem alcança as lojas por escopo cedido, elas não são "contas" dele — são as lojas
  // que ele acompanha, e é assim que a agência fala delas. O rótulo não cita o **time**
  // porque a credencial cedida ao cliente (D-060) chega por aqui sem participar de nenhum.
  const rotuloDasContas = isTeamScoped
    ? "Lojas que você acompanha"
    : isAgencyUser
      ? "Subcontas"
      : "Contas";

  const handleSelect = async (account: Account) => {
    if (!selectedAccount || account.id !== selectedAccount.id) {
      await switchAccount(account.id, { redirect: redirectOnSwitch });
    }
    onOpenChange(false);
  };

  const handleBackToMain = () => {
    clearSelectedAccount({ redirect: redirectOnSwitch });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Selecionar Conta</DialogTitle>
          <DialogDescription>
            {selectedAccount
              ? "Selecione uma conta diferente para visualizar seus dados"
              : "Escolha uma conta para visualizar seus dados"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : accounts.length === 0 && !canSwitchAccounts ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma conta cadastrada
          </p>
        ) : (
          <>
            <ScrollArea className="max-h-[55vh]">
              {/* Conta Principal — a da agência, que gerencia as subcontas.
                  <M-214> Um lojista não tem isso: todas as contas dele estão na lista
                  abaixo, e voltar para "nenhuma conta" deixaria as telas sem tenant. */}
              {isAgencyUser && (
                <button
                  onClick={handleBackToMain}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left mb-1"
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-md shrink-0">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-foreground">
                      Conta Principal
                    </span>
                    <p className="text-xs text-muted-foreground truncate">
                      Gerenciar contas da Agência
                    </p>
                  </div>
                </button>
              )}

              {accounts.length > 0 && (
                <>
                  {isAgencyUser && <Separator className="my-2" />}

                  <p className="text-xs text-muted-foreground px-1 pb-1">
                    {rotuloDasContas}
                  </p>

                  <div className="space-y-1">
                    {accounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => handleSelect(account)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="flex items-center justify-center w-8 h-8 bg-[#2869C1]/10 rounded-md shrink-0">
                          <Building2 className="h-4 w-4 text-[#2869C1]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground truncate">
                              {account.name}
                            </span>
                            {account.status && (
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                                  account.status === "ativa" ||
                                  account.status === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                              >
                                {account.status === "active"
                                  ? "ativa"
                                  : account.status}
                              </span>
                            )}
                          </div>
                          {account.cnpj && (
                            <span className="text-xs text-muted-foreground truncate">
                              CNPJ: {account.cnpj}
                            </span>
                          )}
                        </div>
                        {selectedAccount?.id === account.id && (
                          <Check className="h-4 w-4 text-[#2869C1] shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </ScrollArea>
            <div className="text-xs text-muted-foreground text-center pt-3 border-t">
              {accounts.length}{" "}
              {isTeamScoped ? "loja" : isAgencyUser ? "subconta" : "conta"}
              {accounts.length !== 1 ? "s" : ""} disponíve
              {accounts.length !== 1 ? "is" : "l"}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AccountSelectionModal;
